import {
  WSActions,
  WSCard,
  WSForm,
  WSList,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { INVOICING_PATH } from "../..";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useGetPayerEngagementQuery } from "../../../../query/payerEngagements/queries/useGetPayerEngagementQuery";
import { usePayerQuery } from "../../../../query/payers/queries/usePayerQuery";
import { useInvoiceQuery } from "../../../../query/payments/queries";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import { useQueryWorkItem } from "../../../../query/workItem/queries/useQueryWorkItem";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { Layout } from "../../../../shared/components/Layout";
import { CardWorkItems } from "../../../WorkLogs/components/CardWorkItems";
import { WorkLogEngagementDetails } from "../../../WorkLogs/components/WorkLogEngagementDetails";
import { AdditionalDetails } from "./AdditionalDetails";
import { useMutationSubmit } from "./useMutationSubmit";
import { useQueryInvoicingConfigForPayee } from "../../../../query/invoicingConfig/queries/useQueryInvoicingConfigForPayee";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../../../Invoices/selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { AlertClientNotAllowedSubmission } from "../../../../Invoices/components/AlertClientNotAllowedSubmission";
import React from "react";
import { useUserId } from "../../../../query/hooks/helpers";

export type FormDataAttachment = {
  fileId: string;
  remove?: string;
};

export type FormDataWorkLogInvoice = {
  notes: string;
  attachments: FormDataAttachment[];
};

export const RouteEditWorkLogInvoice: React.FC<
  RouteComponentProps<{ invoiceId: string }>
> = props => {
  const userId = useUserId();
  const history = useHistory();
  const invoiceId = props.match.params.invoiceId;

  const queryInvoice = useInvoiceQuery(invoiceId);
  const queryWorkItem = useQueryWorkItem(
    queryInvoice.data?.lineItems[0].labels?.workItemId as string,
    {
      enabled: !!queryInvoice.data?.lineItems[0].labels.workItemId
    }
  );

  const workLogId = queryWorkItem.data?.workLogId;
  const payerId = queryInvoice.data?.clientId;
  const payerPayeeEngagementId = queryInvoice.data?.memberClientId as
    | string
    | undefined;

  const queryPayer = usePayerQuery(payerId!, {
    enabled: !!payerId
  });
  const queryPayerPayeeEngagement = useGetPayerEngagementQuery(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );

  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  const queryInvoicingConfigForPayee = useQueryInvoicingConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  const invoice = queryInvoice.data;
  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;
  const workDefinitions = queryWorkDefinitions.data || [];
  const workItems = queryWorkItems.data || [];
  const isPayeeNotAllowedToInitiateInvoice =
    !selectorIsPayeeAllowedToInitiateInvoice(
      queryInvoicingConfigForPayee.data,
      userId
    );

  const [save, saveMeta] = useMutationSubmit(invoiceId);
  const [send, sendMeta] = useMutationSubmit(invoiceId, {
    sendInvoice: true
  });

  if (!invoice || !payer || !payerPayeeEngagement) {
    return (
      <Layout title="Edit invoice" wide>
        <WSCard shimmer>
          <WSSectionToolbar title="Engagement" />
        </WSCard>
      </Layout>
    );
  }

  return (
    <Layout
      title="Edit invoice"
      wide
      onClose={() => {
        history.push(INVOICING_PATH);
      }}
    >
      <WSForm<FormDataWorkLogInvoice>
        defaultValues={{
          notes: invoice.invoiceNotes || "",
          attachments:
            invoice.attachments?.customAttachmentIds?.map(id => ({
              fileId: id
            })) || []
        }}
      >
        <WSList gap="2XL">
          <WorkLogEngagementDetails
            payer={payer}
            payerPayeeEngagement={payerPayeeEngagement}
          />
          {isPayeeNotAllowedToInitiateInvoice && (
            <AlertClientNotAllowedSubmission />
          )}

          <CardWorkItems
            shimmer={
              queryWorkItems.isInitialLoading ||
              queryWorkDefinitions.isInitialLoading
            }
            noEdit
            workLogId={workLogId!}
            payerId={payerId!}
            payerPayeeEngagementId={payerPayeeEngagementId!}
            workItems={workItems}
            workDefinitions={workDefinitions}
            resources={{
              payerPayeeEngagement
            }}
          />

          <AdditionalDetails notesInitialVisible={!!invoice.invoiceNotes} />

          <WSErrorMessage
            contextKey="InvoiceForm"
            error={saveMeta.error || sendMeta.error}
          />

          <WSForm.Context>
            {context => (
              <WSActions
                alignment="fill"
                disabled={isPayeeNotAllowedToInitiateInvoice}
                buttons={
                  invoice.status === InvoiceStatus.Open
                    ? [
                        {
                          label: "Save invoice",
                          loading: saveMeta.isLoading,
                          type: "button",
                          onClick: () => {
                            context.handleSubmit(values => {
                              save(values as FormDataWorkLogInvoice);
                            })();
                          }
                        }
                      ]
                    : [
                        {
                          kind: "Tertiary",
                          label: "Save draft and exit",
                          loading: saveMeta.isLoading,
                          type: "button",
                          onClick: () => {
                            context.handleSubmit(values => {
                              save(values as FormDataWorkLogInvoice);
                            })();
                          }
                        },
                        {
                          label: "Send invoice",
                          loading: sendMeta.isLoading,
                          type: "button",
                          onClick: () => {
                            context.handleSubmit(values => {
                              send(values as FormDataWorkLogInvoice);
                            })();
                          }
                        }
                      ]
                }
              />
            )}
          </WSForm.Context>
        </WSList>
      </WSForm>
    </Layout>
  );
};
